import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import pl from 'date-fns/locale/pl';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from '../../translations';
import { AmountField, DiscountField, AmountNote } from '../../ui/AmountField';
import { formatMoney } from '../../utils/format';
import { pokazDniPlatne } from '../../utils/dniPlatne';
import InventoryModal from '../../components/InventoryModal';

import {
  CalendarIcon, UserIcon, MailIcon, PackageIcon, TagIcon,
  DocumentIcon, PlusIcon, TrashIcon, XIcon, CheckIcon,
  PencilIcon, CreditCardIcon,
} from './icons';
import {
  DatepickerTheme, Container, Header, Title, TitleIcon, Subtitle,
  TabNavigation, TabButton, Message,
  ClientPanel, Panel, TermPanel, PaymentPanel,
  PanelHeader, PanelIcon, PanelTitle,
  Grid, RangeLabelRow, ClearRangeButton, InputWrapper, Label, Input,
  SuggestionsList, SuggestionItem,
  PaymentGrid,
  EquipmentButton, EquipmentList, EquipmentItem, EquipmentInfo, RemoveButton,
  EmptyState, SummaryTable, SummaryRow, SummaryLabel, SummaryValue,
  ButtonGroup, PrimaryButton, SuccessButton, SecondaryButton,
  HistorySection, HistoryHeader, HistoryTitle, HistoryList, HistoryEmpty,
  FiltersPanel, FiltersRow, FilterGroup, FilterLabel, FilterInput, ClearFiltersButton,
  OfferCard, OfferCardHeader, OfferAvatar, OfferInfo, OfferStats, OfferActions,
  OfferDeleteButton, OfferActionButton, OfferRow, OfferName, OfferMeta, OfferPrice,
  OfferBadge, OfferDate,
  PaginationContainer, PaginationButton, PaginationInfo,
  OfferFormModal, OfferFormContainer, OfferFormHeader, OfferFormTitle,
  CompactGrid, FormPanel, FormPanelHeader, FormPanelIcon, FormPanelTitle,
  FormInputWrapper, FormLabel, FormValue,
  ModalOverlay, ModalContent, ModalHeader, ModalTitle, CloseButton,
  EditHeaderButton,
} from './GenerateOffer.styles';
import { getEquipmentDailyRate, savedOfferRowFor, savedOfferGroups, ymdToPl, isOfferExpired } from './utils';
import { buildPriceBreakdown, groupEquipmentForDisplay } from '../../utils/priceBreakdown';
import { ItemRate, RuleBadges, BundleFrame, QuotePendingNote, SummaryBreakdownRows } from '../../components/booking/PriceBreakdown';
import FilterDropdown from './components/FilterDropdown';
import CustomRangeInput from './components/CustomRangeInput';
import { ConfirmSendModal, ConfirmDeleteModal, SentOffersModal } from './components/Modals';
import useOfferForm from './hooks/useOfferForm';
import useOffersHistory from './hooks/useOffersHistory';
import useClientSuggestions from './hooks/useClientSuggestions';
import useOfferActions from './hooks/useOfferActions';
import { historyRows } from './clientApi';
import { TylkoOdczytWPrzerwie } from '../../components/przerwa/KontoWPrzerwie';

registerLocale('pl', pl);

// Limity pól klienta, te same co na serwerze (Login/backend/lib/offerDocument.js, przegląd
// bezpieczeństwa 12.10.2026): dłuższego tekstu serwer nie przyjmie, więc pole go nie przyjmuje.
const LIMIT_IMIENIA = 100;
const LIMIT_EMAILA = 254;

function GenerateOffer() {
  const { t, currentLanguage } = useTranslation();

  const [activeTab, setActiveTab] = useState('form');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const containerTopRef = useRef(null);

  // Auto-hide notifications
  useEffect(() => {
    if (!message && !error) return;
    const id = setTimeout(() => { setMessage(''); setError(''); }, 5000);
    return () => clearTimeout(id);
  }, [message, error]);

  const scrollToTop = useCallback(() => {
    if (typeof window === 'undefined') return;
    const docEl = document.scrollingElement || document.documentElement || document.body;
    const selectors = ['#root', '#app', '.app', '.main-content'];
    const scroll = () => {
      // `behavior:'smooth'` na WSZYSTKICH wywołaniach — wcześniej ta druga runda (po 300ms,
      // "na wypadek gdyby layout się zmienił") ustawiała `scrollTop = 0` na twardo, co ucinało
      // animację w połowie i dawało "płynnie, a potem szarpnięcie" zamiast jednej gładkiej jazdy.
      try { docEl.scrollTo({ top: 0, behavior: 'smooth' }); } catch { docEl.scrollTop = 0; }
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
      selectors.forEach(sel => {
        const c = document.querySelector(sel);
        if (c) { try { c.scrollTo({ top: 0, behavior: 'smooth' }); } catch { c.scrollTop = 0; } }
      });
      if (containerTopRef.current) {
        try { containerTopRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch {}
      }
    };
    scroll();
    setTimeout(scroll, 300);
  }, []);

  // Form hook
  const offerForm = useOfferForm({ setMessage, setError, setFieldErrors, scrollToTop });
  const {
    form, setForm, dateFrom, setDateFrom, dateTo, setDateTo,
    selectedEquipment, setSelectedEquipment, equipmentList, showEquipmentModal, setShowEquipmentModal,
    currentOfferId, availabilityWarning, startOfToday,
    days, chargeableDays, selectedEq, isLongTermRental, totalPrice, priceQuoteData, quotePending, totalBail,
    discountNumber, discountErrorText, advanceErrorText,
    discountValue, toPay, advanceAmount, effectiveToPay, isOfferIncomplete,
    setDiscount, setAdvance, setDiscountUnit, checkDiscountOnBlur, checkAdvanceOnBlur,
    computeOfferDeposit, buildOfferData, validateOfferInputs,
    openEquipmentModal, removeEquipment, clearFormInputs, resetOfferForm,
    loadOfferIntoForm, loadOfferIntoFormSilent, restoreFormSnapshot,
    createReservationFromOffer, getSelectedOfferTotals,
  } = offerForm;

  // History hook
  const offersHistory = useOffersHistory({ setMessage, setError, scrollToTop });
  const {
    offersHistory: historyList, setOffersHistory, loadingHistory, historyError,
    historyCurrentPage, setHistoryCurrentPage, historyFilters, setHistoryFilters,
    showOfferModal, setShowOfferModal, selectedOffer, setSelectedOffer,
    isEditingOfferModal, setIsEditingOfferModal, savingOfferModal, setSavingOfferModal,
    showDeleteConfirmModal, setShowDeleteConfirmModal, offerToDelete, setOfferToDelete,
    fetchOffersHistory, promptDeleteOffer, confirmDeleteFromModal, openOfferModal,
    getOfferNumber, filteredOffers, historyTotalPages, paginatedFilteredOffers,
    clearHistoryFilters, hasActiveFilters,
  } = offersHistory;

  // Cross-hook operation: cancel editing in offer modal
  const cancelEditOfferInModal = useCallback(() => {
    setIsEditingOfferModal(false);
    setSavingOfferModal(false);
    restoreFormSnapshot();
  }, [setIsEditingOfferModal, setSavingOfferModal, restoreFormSnapshot]);

  // Client suggestions hook
  const clientSugg = useClientSuggestions({ setForm });
  const {
    clientSuggestions, showClientSuggestions, suggestionsPos,
    lastnameWrapperRef, emailWrapperRef, suggestionRef, suggestionsListRef,
    handleLastnameChange, handleEmailChange, handleClientSuggestionClick,
  } = clientSugg;

  // Offer actions hook
  const actions = useOfferActions({
    form, dateFrom, dateTo, chargeableDays, selectedEq, selectedEquipment,
    discountValue, totalPrice, toPay, advanceAmount, totalBail,
    currentOfferId, setCurrentOfferId: offerForm.setCurrentOfferId,
    buildOfferData, validateOfferInputs, resetOfferForm, computeOfferDeposit,
    fetchOffersHistory, setOffersHistory,
    selectedOffer, setSelectedOffer,
    setMessage, setError, setFieldErrors, scrollToTop,
    cancelEditOfferInModal, savingOfferModal, setSavingOfferModal,
  });
  const {
    savingOffer, sendingOffer,
    showSentOffersModal, setShowSentOffersModal, sentOffers, loadingSentOffers,
    showConfirmSendModal, setShowConfirmSendModal,
    showConfirmSendHistoryModal, setShowConfirmSendHistoryModal,
    offerToSendFromHistory, setOfferToSendFromHistory,
    handleGenerateOffer, handleSaveOffer, fetchSentOffers, handleSendEmailOffer, confirmSendEmail,
    handleGenerateOfferFromHistory, handleSendEmailFromHistory, saveOfferFromModal,
  } = actions;

  // Fetch history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && historyList.length === 0 && !loadingHistory) {
      fetchOffersHistory();
    }
  }, [activeTab]);

  const handleClearForm = () => {
    clearFormInputs();
    setMessage('');
    setError('');
  };

  // Computed values for selected offer detail modal
  const { totalDays: selectedOfferTotalDays, chargeableDays: selectedOfferChargeableDays, isLongTerm: selectedOfferIsLongTerm } =
    getSelectedOfferTotals(selectedOffer);
  const selectedOfferDeposit = computeOfferDeposit(selectedOffer);

  // Wiersze PDF oferty otwartej z Historii (12.10.2026). Lista ofert ich nie niesie (przegląd
  // bezpieczeństwa), więc okno pyta o nie przy otwarciu, tą samą funkcją, z której korzystają
  // „Generuj ofertę” i „Wyślij mailem” (clientApi.js#historyRows). Te same wiersze idą potem do
  // dokumentu, więc okno i PDF nie mogą pokazać różnych stawek. Odpowiedź dla oferty, której
  // okno już zamknięto albo zmieniono, jest odrzucana.
  const [historyWindow, setHistoryWindow] = useState({ offerId: null, rows: null, pending: false });
  useEffect(() => {
    if (!showOfferModal || !selectedOffer || isEditingOfferModal) return undefined;
    let alive = true;
    const offerId = selectedOffer.id;
    setHistoryWindow({ offerId, rows: null, pending: true });
    historyRows(selectedOffer)
      .then((rows) => { if (alive) setHistoryWindow({ offerId, rows: Array.isArray(rows) && rows.length ? rows : null, pending: false }); })
      .catch(() => { if (alive) setHistoryWindow({ offerId, rows: null, pending: false }); });
    return () => { alive = false; };
  }, [selectedOffer, showOfferModal, isEditingOfferModal]);
  const historyRowsPending = !!selectedOffer
    && (historyWindow.offerId !== selectedOffer.id || historyWindow.pending);
  const windowOffer = selectedOffer && historyWindow.offerId === selectedOffer.id && historyWindow.rows
    ? { ...selectedOffer, equipment_rows: historyWindow.rows }
    : selectedOffer;

  // Stawka sztuki w oknie oferty z Historii: z wierszy dokumentu, czyli ta sama co w PDF (test
  // odbiorczy 11.10.2026: okno pokazywało stawki bazowe obok ceny z cennikiem). Bez wierszy (nie
  // udało się ich pobrać) stawka z karty sprzętu, jak dotąd.
  const historyItemRate = (eq) => {
    if (historyRowsPending) return t('priceBreakdown.recalculating');
    const row = savedOfferRowFor(windowOffer, eq.id);
    const amount = row ? row.cenaDzien : Number(getEquipmentDailyRate(eq, selectedOfferIsLongTerm) || 0).toFixed(2);
    return t('generateOffer.history.ratePerDay', { amount });
  };

  // P7.5: price-visibility view-model; null (no/stale quote) → UI identical to pre-P7.5
  const priceBreakdown = buildPriceBreakdown(priceQuoteData, selectedEq, eq => getEquipmentDailyRate(eq, isLongTermRental));
  const equipmentGroups = groupEquipmentForDisplay(selectedEq, priceBreakdown);
  const itemView = (eq) => (priceBreakdown ? priceBreakdown.itemsById[eq.id] : null);

  // U.26: pola rabatu i zaliczki. Formularz oferty i okno edycji oferty pracują na TYM SAMYM stanie
  // (`form`), więc renderują te same pola; różni je tylko `id` (oba bywają w drzewie naraz).
  const renderDiscountField = (id) => (
    <div>
      <DiscountField
        id={id}
        value={form.discount}
        unit={form.discountUnit}
        onChange={setDiscount}
        onUnitChange={setDiscountUnit}
        onBlur={checkDiscountOnBlur}
        placeholder={form.discountUnit === 'PLN' ? t('amountField.discountPlaceholderPln') : t('amountField.discountPlaceholderPercent')}
        invalid={!!discountErrorText}
        describedBy={`${id}-note`}
        dataCy={id}
      />
      <AmountNote id={`${id}-note`} data-error={!!discountErrorText} data-cy={`${id}-note`}>
        {discountErrorText
          || (discountNumber > 0 && totalPrice > 0
            ? t('amountField.afterDiscount', { amount: formatMoney(toPay, { locale: currentLanguage }) })
            : null)}
      </AmountNote>
    </div>
  );
  const renderAdvanceField = (id) => (
    <div>
      <AmountField
        id={id}
        name="advanceAmount"
        value={form.advanceAmount}
        onChange={setAdvance}
        onBlur={checkAdvanceOnBlur}
        placeholder={t('amountField.advancePlaceholder')}
        invalid={!!advanceErrorText}
        describedBy={`${id}-note`}
        dataCy={id}
      />
      <AmountNote id={`${id}-note`} data-error={!!advanceErrorText} data-cy={`${id}-note`}>
        {advanceErrorText}
      </AmountNote>
    </div>
  );

  return (
    <>
      <DatepickerTheme />
      <Container>
        <span ref={containerTopRef} />
        <Header>
          <Title>
            <TitleIcon><DocumentIcon /></TitleIcon>
            {t('generateOffer.title')}
          </Title>
          <Subtitle>{t('generateOffer.subtitle')}</Subtitle>
        </Header>

        <TabNavigation data-tour="offers-tabs">
          <TabButton active={activeTab === 'form'} onClick={() => setActiveTab('form')} data-cy="offer-tab-form">
            <DocumentIcon /> Formularz oferty
          </TabButton>
          <TabButton
            active={activeTab === 'history'}
            onClick={() => {
              setActiveTab('history');
              if (historyList.length === 0 && !loadingHistory) fetchOffersHistory();
            }}
            data-cy="offer-tab-history"
          >
            <MailIcon /> Historia ofert
          </TabButton>
        </TabNavigation>

        {message && <Message><CheckIcon />{message}</Message>}
        {error && <Message error><XIcon />{error}</Message>}
        {availabilityWarning && <Message error><XIcon />{availabilityWarning}</Message>}

        {activeTab === 'form' && (
          // F.11: w przerwie formularz tylko do podglądu (historia ofert zostaje do przeglądania).
          <TylkoOdczytWPrzerwie>
            <ClientPanel data-tour="offers-client">
              <PanelHeader>
                <PanelIcon $tone="steel"><UserIcon /></PanelIcon>
                <PanelTitle>Dane klienta</PanelTitle>
              </PanelHeader>
              {/* Ten Grid ma tylko 3 pola (inaczej niz odpowiedniki w reservation.js, ktore
                  maja ich 6), a auto-fit/1fr rozciaga kolumny na cala dostepna szerokosc paneli —
                  przy mniejszej liczbie pol robily sie przez to widocznie szersze/wieksze niz
                  te same pola gdzie indziej. Gorny limit szerokosci kolumny naprawia wrazenie
                  "przybliżenia" bez ruszania wspolnego komponentu Grid (uzywanego ponizej
                  do ukladu calych paneli Termin/Platnosc, gdzie ma zostac szeroki). */}
              <Grid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 320px))' }}>
                <InputWrapper>
                  <Label><UserIcon /> Imię</Label>
                  <Input
                    type="text"
                    placeholder="Jan"
                    maxLength={LIMIT_IMIENIA}
                    value={form.firstName}
                    onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                    data-error={!!fieldErrors.firstName}
                    data-cy="offer-firstname"
                  />
                </InputWrapper>

                <InputWrapper ref={node => { suggestionRef.current = node; lastnameWrapperRef.current = node; }}>
                  <Label><UserIcon /> Nazwisko</Label>
                  <Input
                    type="text"
                    placeholder="Kowalski"
                    maxLength={LIMIT_IMIENIA}
                    value={form.lastName}
                    onChange={handleLastnameChange}
                    autoComplete="off"
                    data-error={!!fieldErrors.lastName}
                    data-cy="offer-lastname"
                  />
                  {showClientSuggestions && clientSuggestions.length > 0 && suggestionsPos &&
                    ReactDOM.createPortal(
                      <SuggestionsList
                        ref={suggestionsListRef}
                        style={{ position: 'absolute', top: suggestionsPos.top, left: suggestionsPos.left, width: suggestionsPos.width, zIndex: 6500 }}
                      >
                        {clientSuggestions.map(c => (
                          <SuggestionItem key={c.id} onClick={() => handleClientSuggestionClick(c)} data-cy="client-suggestion">
                            <strong style={{ fontWeight: 700 }}>{c.lastname} {c.firstname ? c.firstname : ''}</strong>
                            <span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>{c.email || '-'}</span>
                          </SuggestionItem>
                        ))}
                      </SuggestionsList>,
                      document.body
                    )
                  }
                </InputWrapper>

                <InputWrapper ref={emailWrapperRef}>
                  <Label><MailIcon /> E-mail</Label>
                  <Input
                    type="email"
                    placeholder="jan.kowalski@example.com"
                    maxLength={LIMIT_EMAILA}
                    value={form.email}
                    onChange={handleEmailChange}
                    autoComplete="off"
                    data-error={!!fieldErrors.email}
                    data-cy="offer-email"
                  />
                </InputWrapper>
              </Grid>
            </ClientPanel>

            <Grid>
              <TermPanel>
                <PanelHeader>
                  <PanelIcon $tone="dusk"><CalendarIcon /></PanelIcon>
                  <PanelTitle>Termin wynajmu</PanelTitle>
                </PanelHeader>
                <InputWrapper style={{ flex: 1 }}>
                  <RangeLabelRow>
                    <Label style={{ marginBottom: 0 }}><CalendarIcon /> Zakres dat*</Label>
                    <ClearRangeButton type="button" onClick={() => { setDateFrom(null); setDateTo(null); }} disabled={!dateFrom && !dateTo} title="Wyczyść daty">
                      Wyczyść
                    </ClearRangeButton>
                  </RangeLabelRow>
                  <DatePicker
                    selected={dateFrom}
                    onChange={(dates) => { const [start, end] = dates; setDateFrom(start); setDateTo(end); }}
                    startDate={dateFrom}
                    endDate={dateTo}
                    selectsRange
                    locale="pl"
                    minDate={startOfToday}
                    filterDate={d => d >= startOfToday}
                    dateFormat="yyyy-MM-dd"
                    shouldCloseOnSelect={false}
                    inline={false}
                    popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                    customInput={<CustomRangeInput start={dateFrom} end={dateTo} placeholderStart="Od" placeholderEnd="Do" />}
                    dayClassName={(date) => {
                      const classes = [];
                      const day = date.getDay();
                      if (day === 0 || day === 6) classes.push('weekend');
                      if (date < startOfToday && date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear()) {
                        classes.push('past-day');
                      }
                      return classes.join(' ');
                    }}
                  />
                </InputWrapper>
              </TermPanel>

              <PaymentPanel>
                <PanelHeader>
                  <PanelIcon $tone="gold"><TagIcon /></PanelIcon>
                  <PanelTitle>Rabat i płatność</PanelTitle>
                </PanelHeader>
                <PaymentGrid>
                  {/* U.26: te same pola co w Nowej rezerwacji (ui/AmountField.js) — surowy tekst,
                      przecinek dziesiętny, nic nie jest przycinane w trakcie pisania. */}
                  <InputWrapper style={{ minWidth: 0 }}>
                    <Label htmlFor="offer-discount"><TagIcon /> Rabat</Label>
                    {renderDiscountField('offer-discount')}
                  </InputWrapper>
                  <InputWrapper style={{ minWidth: 0 }}>
                    <Label htmlFor="offer-advance"><CreditCardIcon /> Zaliczka</Label>
                    {renderAdvanceField('offer-advance')}
                  </InputWrapper>
                </PaymentGrid>
              </PaymentPanel>
            </Grid>

            <Panel>
              <PanelHeader>
                <PanelIcon $tone="pine"><PackageIcon /></PanelIcon>
                <PanelTitle>Wybrany sprzęt*</PanelTitle>
                <EquipmentButton type="button" enabled={!!dateFrom && !!dateTo} onClick={openEquipmentModal} disabled={!dateFrom || !dateTo} data-cy="offer-open-equipment">
                  <PlusIcon /> Wybierz sprzęt
                </EquipmentButton>
              </PanelHeader>
              {selectedEquipment.length > 0 ? (
                <EquipmentList>
                  {equipmentGroups.map((group, gi) => (
                    <BundleFrame key={group.bundle ? `bundle-${gi}` : `eq-${group.items[0].id}`} bundle={group.bundle}>
                      {group.items.map(eq => (
                        <EquipmentItem key={eq.id}>
                          <EquipmentInfo>
                            <PackageIcon />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                                <span style={{ fontWeight: 700 }}>{eq.name}</span>
                                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                  ({[eq.inventory_number, eq.size, eq.brand, eq.category, eq.class].filter(Boolean).join(', ')})
                                </span>
                              </div>
                            </div>
                          </EquipmentInfo>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: 'var(--primary)' }}>
                                <ItemRate item={itemView(eq)} baseText={`${getEquipmentDailyRate(eq, isLongTermRental).toFixed(2)} zł/dzień`} />
                              </div>
                              <RuleBadges item={itemView(eq)} />
                            </div>
                            <RemoveButton onClick={() => removeEquipment(eq.id)}><TrashIcon /></RemoveButton>
                          </div>
                        </EquipmentItem>
                      ))}
                    </BundleFrame>
                  ))}
                </EquipmentList>
              ) : (
                <EmptyState>
                  <div className="icon"><PackageIcon /></div>
                  <div className="text">Nie wybrano sprzętu do oferty</div>
                </EmptyState>
              )}
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelIcon $tone="clay"><DocumentIcon /></PanelIcon>
                <PanelTitle>Podsumowanie</PanelTitle>
              </PanelHeader>
              <SummaryTable>
                <SummaryRow>
                  <SummaryLabel>Ilość dni</SummaryLabel>
                  <SummaryValue data-cy="offer-days">{days} {t('pricing.paidDays', { days: pokazDniPlatne(chargeableDays) })}</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Cena sprzętu</SummaryLabel>
                  <SummaryValue>{totalPrice.toFixed(2)} zł<QuotePendingNote pending={quotePending} /></SummaryValue>
                </SummaryRow>
                <SummaryBreakdownRows breakdown={priceBreakdown} Row={SummaryRow} Label={SummaryLabel} Value={SummaryValue} />
                <SummaryRow>
                  <SummaryLabel>Rabat ({form.discountUnit === '%' ? `${discountNumber}%` : `${discountNumber} zł`})</SummaryLabel>
                  <SummaryValue>-{discountValue.toFixed(2)} zł</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Depozyt zwrotny</SummaryLabel>
                  <SummaryValue>{Number(totalBail || 0).toFixed(2)} zł</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Zaliczka</SummaryLabel>
                  <SummaryValue>{advanceAmount.toFixed(2)} zł</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Pozostało do zapłaty</SummaryLabel>
                  <SummaryValue>{effectiveToPay.toFixed(2)} zł</SummaryValue>
                </SummaryRow>
              </SummaryTable>
            </Panel>

            <ButtonGroup>
              <SecondaryButton onClick={handleClearForm} title="Wyczyść formularz" data-cy="offer-clear">
                <XIcon /> Wyczyść formularz
              </SecondaryButton>
              <SecondaryButton
                onClick={handleSendEmailOffer}
                // W trakcie przeliczania ceny przyciski czekają, zamiast odrzucać kliknięcie
                // komunikatem (CI 11.10.2026: wysyłka tuż po wyborze sprzętu trafiała w przeliczanie).
                disabled={isOfferIncomplete || sendingOffer || quotePending}
                title={quotePending ? t('generateOffer.quotePending') : undefined}
                style={{ background: 'var(--success)', color: 'var(--primary-contrast)', border: 'none', boxShadow: 'var(--shadow-sm)' }}
                data-cy="offer-send-email"
              >
                <MailIcon /> {sendingOffer ? 'Wysyłanie...' : 'Wyślij ofertę emailem'}
              </SecondaryButton>
              <PrimaryButton onClick={handleGenerateOffer} disabled={isOfferIncomplete || quotePending} title={quotePending ? t('generateOffer.quotePending') : undefined} data-cy="offer-generate">
                <DocumentIcon /> Generuj ofertę
              </PrimaryButton>
              <SuccessButton onClick={handleSaveOffer} disabled={isOfferIncomplete || savingOffer || quotePending} title={quotePending ? t('generateOffer.quotePending') : undefined} data-cy="offer-save">
                <CheckIcon /> {savingOffer ? 'Zapisywanie...' : 'Zapisz'}
              </SuccessButton>
            </ButtonGroup>
          </TylkoOdczytWPrzerwie>
        )}

        {activeTab === 'history' && (
          <HistorySection>
            <HistoryHeader>
              <HistoryTitle><MailIcon /> Historia wysłanych ofert</HistoryTitle>
              <FiltersPanel data-cy="offer-history-filters">
                <FiltersRow>
                  <FilterGroup>
                    <FilterLabel>Status:</FilterLabel>
                    <FilterDropdown
                      value={historyFilters.status}
                      onChange={(val) => setHistoryFilters(prev => ({ ...prev, status: val }))}
                      options={[
                        { value: 'all', label: 'Wszystkie' },
                        { value: 'sent', label: 'Wysłane' },
                        { value: 'draft', label: 'Niewysłane' },
                      ]}
                      placeholder="Wybierz status"
                      showSearch={false}
                    />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Klient:</FilterLabel>
                    <FilterInput type="text" placeholder="Wyszukaj klienta..." value={historyFilters.clientSearch} onChange={(e) => setHistoryFilters(prev => ({ ...prev, clientSearch: e.target.value }))} data-cy="offer-history-search" />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Numer oferty:</FilterLabel>
                    <FilterInput type="text" placeholder="Np. O/2026/03" value={historyFilters.offerNumberSearch} onChange={(e) => setHistoryFilters(prev => ({ ...prev, offerNumberSearch: e.target.value }))} data-cy="offer-number-search" />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Data od:</FilterLabel>
                    <FilterInput type="date" value={historyFilters.dateFrom} onChange={(e) => setHistoryFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Data do:</FilterLabel>
                    <FilterInput type="date" value={historyFilters.dateTo} onChange={(e) => setHistoryFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Cena min:</FilterLabel>
                    <FilterInput type="number" placeholder="0" value={historyFilters.minPrice} onChange={(e) => setHistoryFilters(prev => ({ ...prev, minPrice: e.target.value }))} onWheel={(e) => e.currentTarget.blur()} />
                  </FilterGroup>
                  <FilterGroup>
                    <FilterLabel>Cena max:</FilterLabel>
                    <FilterInput type="number" placeholder="9999" value={historyFilters.maxPrice} onChange={(e) => setHistoryFilters(prev => ({ ...prev, maxPrice: e.target.value }))} onWheel={(e) => e.currentTarget.blur()} />
                  </FilterGroup>
                  {hasActiveFilters() && (
                    <ClearFiltersButton onClick={clearHistoryFilters}><XIcon /> Wyczyść filtry</ClearFiltersButton>
                  )}
                </FiltersRow>
              </FiltersPanel>
            </HistoryHeader>

            {loadingHistory ? (
              <HistoryEmpty>Ładowanie ofert...</HistoryEmpty>
            ) : historyError ? (
              <HistoryEmpty style={{ color: 'var(--danger)' }}><XIcon /> {historyError}</HistoryEmpty>
            ) : filteredOffers.length === 0 ? (
              <HistoryEmpty>
                {historyList.length === 0 ? 'Brak wysłanych ofert' : 'Nie znaleziono ofert pasujących do filtrów'}
              </HistoryEmpty>
            ) : (
              <>
                <HistoryList>
                  {paginatedFilteredOffers.map(offer => {
                    const fullName = (() => {
                      const parts = String(offer.client_name || '').trim().split(/\s+/);
                      const first = offer.firstname || offer.firstName || (parts[0] || '');
                      const last = offer.lastname || offer.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');
                      return `${first}${last ? ' ' + last : ''}`.trim();
                    })();
                    const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                    const createdDate = offer.created_at ? new Date(offer.created_at) : null;
                    const offerNumber = getOfferNumber(offer);
                    return (
                      <OfferCard key={offer.id} onClick={() => openOfferModal(offer)} data-cy="offer-card" data-offer-id={offer.id} data-offer-email={offer.email}>
                        <OfferCardHeader>
                          <OfferAvatar>{initials || '??'}</OfferAvatar>
                          <OfferName>{fullName}</OfferName>
                        </OfferCardHeader>
                        <OfferInfo>
                          <OfferMeta><MailIcon /> {offer.email}</OfferMeta>
                          <OfferMeta><CalendarIcon />{new Date(offer.date_from).toLocaleDateString('pl-PL')} - {new Date(offer.date_to).toLocaleDateString('pl-PL')}</OfferMeta>
                        </OfferInfo>
                        <OfferStats style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flex: '1 1 auto', minWidth: '120px' }}>
                            <strong>Nr:</strong> {offerNumber}
                          </div>
                          <OfferPrice style={{ flex: '0 0 auto', margin: 0 }}>
                            {parseFloat(offer.total_price).toFixed(2)} zł
                          </OfferPrice>
                          <OfferDate style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CalendarIcon />
                            {createdDate ? createdDate.toLocaleDateString('pl-PL') : 'Brak daty'}
                          </OfferDate>
                        </OfferStats>
                      </OfferCard>
                    );
                  })}
                </HistoryList>

                {historyTotalPages > 1 && (
                  <PaginationContainer>
                    <PaginationButton onClick={() => setHistoryCurrentPage(p => Math.max(1, p - 1))} disabled={historyCurrentPage === 1}>←</PaginationButton>
                    <PaginationInfo>{historyCurrentPage} / {historyTotalPages}</PaginationInfo>
                    <PaginationButton onClick={() => setHistoryCurrentPage(p => Math.min(historyTotalPages, p + 1))} disabled={historyCurrentPage === historyTotalPages}>→</PaginationButton>
                  </PaginationContainer>
                )}
              </>
            )}
          </HistorySection>
        )}

        {/* Offer detail / edit modal */}
        {showOfferModal && selectedOffer && (
          <OfferFormModal onClick={() => { cancelEditOfferInModal(); setShowOfferModal(false); setSelectedOffer(null); }} data-cy="offer-modal">
            <OfferFormContainer onClick={(e) => e.stopPropagation()} data-cy="offer-modal-content">
              <OfferFormHeader>
                <OfferFormTitle><DocumentIcon /> Szczegóły oferty</OfferFormTitle>
                <CloseButton onClick={() => { cancelEditOfferInModal(); setShowOfferModal(false); setSelectedOffer(null); }}>&times;</CloseButton>
              </OfferFormHeader>

              <div style={{ display: 'grid', gap: '16px' }}>
                {!isEditingOfferModal && selectedOffer.issued_on && (
                  <div data-cy="offer-history-validity" style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                    {selectedOffer.valid_until
                      ? t('generateOffer.history.issuedValidUntil', { issued: ymdToPl(selectedOffer.issued_on), validUntil: ymdToPl(selectedOffer.valid_until) })
                      : t('generateOffer.history.issued', { issued: ymdToPl(selectedOffer.issued_on) })}
                  </div>
                )}
                {/* Oferta zmieniona po wysyłce (12.10.2026): kopia z Historii ma wtedy dzisiejszą datę,
                    więc ostrzeżenie o kopii z pierwotną datą byłoby nieprawdą. */}
                {!isEditingOfferModal && selectedOffer.changed_after_send_at && (
                  <Message $warning data-cy="offer-history-changed" style={{ marginBottom: 0 }}>
                    {t('generateOffer.history.changedAfterSend', { issued: ymdToPl(selectedOffer.issued_on) })}
                  </Message>
                )}
                {!isEditingOfferModal && !selectedOffer.changed_after_send_at && isOfferExpired(selectedOffer) && (
                  <Message $warning data-cy="offer-history-expired" style={{ marginBottom: 0 }}>
                    {t('generateOffer.history.expired', { validUntil: ymdToPl(selectedOffer.valid_until) })}
                  </Message>
                )}
                <Panel>
                  <PanelHeader>
                    <PanelIcon $tone="steel"><UserIcon /></PanelIcon>
                    <PanelTitle>Dane klienta</PanelTitle>
                  </PanelHeader>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <InputWrapper>
                      <Label><UserIcon /> Imię</Label>
                      <Input
                        type="text"
                        value={isEditingOfferModal ? (form.firstName ?? '') : (() => {
                          const parts = String(selectedOffer.client_name || '').trim().split(/\s+/);
                          return selectedOffer.firstname || selectedOffer.firstName || (parts[0] || '');
                        })()}
                        disabled={!isEditingOfferModal}
                        maxLength={LIMIT_IMIENIA}
                        onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <Label><UserIcon /> Nazwisko</Label>
                      <Input
                        type="text"
                        value={isEditingOfferModal ? (form.lastName ?? '') : (() => {
                          const parts = String(selectedOffer.client_name || '').trim().split(/\s+/);
                          return selectedOffer.lastname || selectedOffer.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');
                        })()}
                        disabled={!isEditingOfferModal}
                        maxLength={LIMIT_IMIENIA}
                        onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <Label><MailIcon /> E-mail</Label>
                      <Input
                        type="text"
                        value={isEditingOfferModal ? (form.email ?? '') : (selectedOffer.email ?? '')}
                        disabled={!isEditingOfferModal}
                        maxLength={LIMIT_EMAILA}
                        onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </InputWrapper>
                  </div>
                </Panel>

                <CompactGrid>
                  <FormPanel>
                    <FormPanelHeader>
                      <FormPanelIcon $tone="dusk"><CalendarIcon /></FormPanelIcon>
                      <FormPanelTitle>Termin wynajmu</FormPanelTitle>
                    </FormPanelHeader>
                    <FormInputWrapper>
                      <RangeLabelRow>
                        <FormLabel style={{ marginBottom: 0 }}><CalendarIcon /> Zakres dat</FormLabel>
                        <ClearRangeButton type="button" onClick={() => { setDateFrom(null); setDateTo(null); }} disabled={!dateFrom && !dateTo} title="Wyczyść daty">Wyczyść</ClearRangeButton>
                      </RangeLabelRow>
                      {isEditingOfferModal ? (
                        <div>
                          <DatePicker
                            selected={dateFrom}
                            onChange={(dates) => { const [start, end] = dates; setDateFrom(start); setDateTo(end); }}
                            startDate={dateFrom} endDate={dateTo} selectsRange locale="pl"
                            minDate={startOfToday} filterDate={d => d >= startOfToday}
                            dateFormat="yyyy-MM-dd" shouldCloseOnSelect={false} inline={false}
                            popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                            customInput={<CustomRangeInput start={dateFrom} end={dateTo} placeholderStart="Od" placeholderEnd="Do" />}
                          />
                        </div>
                      ) : (
                        <FormValue>
                          {new Date(selectedOffer.date_from).toLocaleDateString('pl-PL')} → {new Date(selectedOffer.date_to).toLocaleDateString('pl-PL')}
                        </FormValue>
                      )}
                    </FormInputWrapper>
                  </FormPanel>

                  <FormPanel>
                    <FormPanelHeader>
                      <FormPanelIcon $tone="gold"><TagIcon /></FormPanelIcon>
                      <FormPanelTitle>Płatność</FormPanelTitle>
                    </FormPanelHeader>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                      <FormInputWrapper>
                        <FormLabel htmlFor={isEditingOfferModal ? 'offer-modal-discount' : undefined}><TagIcon /> Rabat</FormLabel>
                        {isEditingOfferModal ? renderDiscountField('offer-modal-discount') : (
                          <FormValue>
                            {(() => {
                              const unit = String(selectedOffer.discountUnit || selectedOffer.discount_unit || 'PLN') === '%' ? '%' : 'PLN';
                              const amountPln = Number(selectedOffer.discount_amount || 0) || 0;
                              const subtotal = Number(selectedOffer.subtotal || selectedOffer.cenaCalosc || 0) || 0;
                              if (unit === '%') {
                                const pct = subtotal > 0 ? Number(((amountPln / subtotal) * 100).toFixed(2)) : 0;
                                return `${pct}%`;
                              }
                              return `${amountPln.toFixed(2)} zł`;
                            })()}
                          </FormValue>
                        )}
                      </FormInputWrapper>
                      <FormInputWrapper>
                        <FormLabel htmlFor={isEditingOfferModal ? 'offer-modal-advance' : undefined}><CreditCardIcon /> Zaliczka</FormLabel>
                        {isEditingOfferModal ? renderAdvanceField('offer-modal-advance') : (
                          <FormValue>{parseFloat(selectedOffer.advance_amount || selectedOffer.advance || 0).toFixed(2)} zł</FormValue>
                        )}
                      </FormInputWrapper>
                    </div>
                  </FormPanel>
                </CompactGrid>

                {isEditingOfferModal ? (
                  <Panel>
                    <PanelHeader>
                      <PanelIcon $tone="pine"><PackageIcon /></PanelIcon>
                      <PanelTitle>Wybrany sprzęt</PanelTitle>
                      <EquipmentButton type="button" enabled={!!(dateFrom && dateTo)} onClick={openEquipmentModal} disabled={!(dateFrom && dateTo)} style={{ marginLeft: 'auto' }} data-cy="offer-modal-open-equipment">
                        <PackageIcon /> Wybierz sprzęt
                      </EquipmentButton>
                    </PanelHeader>
                    {selectedEq.length > 0 ? (
                      <EquipmentList>
                        {equipmentGroups.map((group, gi) => (
                          <BundleFrame key={group.bundle ? `bundle-${gi}` : `eq-${group.items[0].id}`} bundle={group.bundle}>
                            {group.items.map((eq) => (
                              <EquipmentItem key={eq.id}>
                                <EquipmentInfo>
                                  <PackageIcon />
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{eq.name || eq.category}</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{[eq.inventory_number, eq.size, eq.brand, eq.class].filter(Boolean).join(', ')}</div>
                                  </div>
                                </EquipmentInfo>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 700, color: 'var(--primary)' }}>
                                      <ItemRate item={itemView(eq)} baseText={`${Number(getEquipmentDailyRate(eq, isLongTermRental) || 0).toFixed(2)} zł/dzień`} />
                                    </div>
                                    <RuleBadges item={itemView(eq)} />
                                  </div>
                                  <RemoveButton type="button" onClick={() => removeEquipment(eq.id)}><TrashIcon /></RemoveButton>
                                </div>
                              </EquipmentItem>
                            ))}
                          </BundleFrame>
                        ))}
                      </EquipmentList>
                    ) : (
                      <EmptyState><div className="icon"><PackageIcon /></div><div className="text">Nie wybrano sprzętu do oferty</div></EmptyState>
                    )}
                  </Panel>
                ) : (
                  Array.isArray(selectedOffer.equipment) && selectedOffer.equipment.length > 0 && (
                    <Panel>
                      <PanelHeader>
                        <PanelIcon $tone="pine"><PackageIcon /></PanelIcon>
                        <PanelTitle>Wybrany sprzęt</PanelTitle>
                      </PanelHeader>
                      <EquipmentList>
                        {savedOfferGroups(windowOffer).map((group, gi) => (
                          <BundleFrame key={group.bundle ? `bundle-${gi}` : `eq-${group.items[0].id}`} bundle={group.bundle}>
                            {group.items.map((eq) => (
                              <EquipmentItem key={eq.id}>
                                <EquipmentInfo>
                                  <PackageIcon />
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{eq.name || eq.category}</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{[eq.inventory_number, eq.size, eq.brand, eq.class].filter(Boolean).join(', ')}</div>
                                  </div>
                                </EquipmentInfo>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 700, color: 'var(--primary)' }} data-cy="offer-history-item-rate">
                                    {group.bundle ? t('generateOffer.history.inBundle') : historyItemRate(eq)}
                                  </div>
                                </div>
                              </EquipmentItem>
                            ))}
                          </BundleFrame>
                        ))}
                      </EquipmentList>
                    </Panel>
                  )
                )}

                <Panel>
                  <PanelHeader>
                    <PanelIcon $tone="clay"><CreditCardIcon /></PanelIcon>
                    <PanelTitle>Podsumowanie</PanelTitle>
                  </PanelHeader>
                  <SummaryTable>
                    <SummaryRow>
                      <SummaryLabel>Ilość dni</SummaryLabel>
                      <SummaryValue data-cy="offer-window-days">{isEditingOfferModal ? `${days || 0} ${t('pricing.paidDays', { days: pokazDniPlatne(chargeableDays) })}` : `${selectedOfferTotalDays || selectedOffer.days || 0} ${t('pricing.paidDays', { days: pokazDniPlatne(selectedOfferChargeableDays) })}`}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Cena sprzętu</SummaryLabel>
                      <SummaryValue>
                        {isEditingOfferModal ? `${Number(totalPrice || 0).toFixed(2)} zł` : `${parseFloat(selectedOffer.subtotal || 0).toFixed(2)} zł`}
                        {isEditingOfferModal && <QuotePendingNote pending={quotePending} />}
                      </SummaryValue>
                    </SummaryRow>
                    {isEditingOfferModal && (
                      <SummaryBreakdownRows breakdown={priceBreakdown} Row={SummaryRow} Label={SummaryLabel} Value={SummaryValue} />
                    )}
                    <SummaryRow>
                      <SummaryLabel>Rabat ({isEditingOfferModal ? (form.discountUnit === '%' ? `${discountNumber}%` : `${discountNumber} zł`) : (() => {
                        const unit = String(selectedOffer.discountUnit || selectedOffer.discount_unit || 'PLN') === '%' ? '%' : 'PLN';
                        const amountPln = Number(selectedOffer.discount_amount || 0) || 0;
                        const subtotal = Number(selectedOffer.subtotal || selectedOffer.cenaCalosc || 0) || 0;
                        if (unit === '%') { const pct = subtotal > 0 ? Number(((amountPln / subtotal) * 100).toFixed(2)) : 0; return `${pct}%`; }
                        return `${amountPln.toFixed(2)} zł`;
                      })()})</SummaryLabel>
                      <SummaryValue>-{isEditingOfferModal ? discountValue.toFixed(2) : parseFloat(selectedOffer.discount_amount || 0).toFixed(2)} zł</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Depozyt zwrotny</SummaryLabel>
                      <SummaryValue data-cy="offer-window-deposit">{isEditingOfferModal ? `${Number(totalBail || 0).toFixed(2)} zł` : `${Number(selectedOfferDeposit || 0).toFixed(2)} zł`}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Zaliczka</SummaryLabel>
                      <SummaryValue>{isEditingOfferModal ? `${Number(advanceAmount || 0).toFixed(2)} zł` : `${parseFloat(selectedOffer.advance_amount || selectedOffer.advance || 0).toFixed(2)} zł`}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Pozostało do zapłaty</SummaryLabel>
                      <SummaryValue>{isEditingOfferModal ? `${Number(toPay || 0).toFixed(2)} zł` : `${parseFloat(selectedOffer.total_price || 0).toFixed(2)} zł`}</SummaryValue>
                    </SummaryRow>
                  </SummaryTable>
                </Panel>

                <ButtonGroup>
                  {isEditingOfferModal ? (
                    <>
                      {/* W edycji PDF z BIEŻĄCEGO formularza (test odbiorczy 11.10.2026): dawniej
                          z zapisanej wersji, więc pobrany dokument nie miał wprowadzonych zmian. */}
                      <SecondaryButton onClick={handleGenerateOffer} disabled={quotePending} title={quotePending ? t('generateOffer.quotePending') : undefined} style={{ background: 'var(--primary)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
                        <DocumentIcon /> Generuj ofertę
                      </SecondaryButton>
                      <SuccessButton onClick={saveOfferFromModal} disabled={savingOfferModal || quotePending} title={quotePending ? t('generateOffer.quotePending') : undefined}>
                        <CheckIcon /> Zapisz zmiany
                      </SuccessButton>
                      <SecondaryButton onClick={cancelEditOfferInModal} disabled={savingOfferModal}>Anuluj</SecondaryButton>
                    </>
                  ) : (
                    <>
                      <SecondaryButton onClick={() => createReservationFromOffer(selectedOffer)} style={{ background: 'var(--primary)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }} data-cy="offer-create-reservation">
                        <PlusIcon /> Stwórz rezerwację
                      </SecondaryButton>
                      {/* Dokument z tych samych wierszy co okno (12.10.2026), więc do czasu ich wczytania
                          przyciski czekają, zamiast liczyć wiersze drugi raz. */}
                      <SecondaryButton onClick={() => handleGenerateOfferFromHistory(windowOffer)} disabled={historyRowsPending} title={historyRowsPending ? t('generateOffer.quotePending') : undefined} style={{ background: 'var(--primary)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }} data-cy="offer-generate-history">
                        <DocumentIcon /> Generuj ofertę
                      </SecondaryButton>
                      <SecondaryButton onClick={() => { setOfferToSendFromHistory(windowOffer); setShowConfirmSendHistoryModal(true); }} disabled={historyRowsPending} title={historyRowsPending ? t('generateOffer.quotePending') : undefined} style={{ background: 'var(--success)', color: 'var(--primary-contrast)', border: 'none', boxShadow: 'var(--shadow-sm)' }} data-cy="offer-send-email-history">
                        <MailIcon /> Wyślij mailem
                      </SecondaryButton>
                      {!isEditingOfferModal && (
                        <EditHeaderButton onClick={() => { loadOfferIntoFormSilent(selectedOffer); setIsEditingOfferModal(true); }} data-cy="offer-edit">
                          <PencilIcon /> Edytuj ofertę
                        </EditHeaderButton>
                      )}
                      <SecondaryButton onClick={(e) => { e.stopPropagation(); setShowOfferModal(false); promptDeleteOffer(selectedOffer); }} style={{ background: 'var(--danger)', color: 'var(--primary-contrast)', border: 'none', boxShadow: 'var(--shadow-sm)' }} data-cy="offer-delete">
                        <TrashIcon /> Usuń ofertę
                      </SecondaryButton>
                      <SecondaryButton onClick={() => { cancelEditOfferInModal(); setShowOfferModal(false); }} data-cy="offer-modal-close">Zamknij</SecondaryButton>
                    </>
                  )}
                </ButtonGroup>
              </div>
            </OfferFormContainer>
          </OfferFormModal>
        )}

        {/* Okna otwierane Z WNETRZA szczegolow oferty (potwierdzenie wysylki) MUSZA stac
            w drzewie ZA oknem szczegolow: wszystkie stoja na --z-overlay, wiec o kolejnosci
            malowania decyduje kolejnosc w DOM. Wczesniej byly przed nim i otwieraly sie pod
            spodem (U.24). Wybor sprzetu od U.27 stoi w portalu na wyzszej warstwie, wiec jego
            ta regula juz nie dotyczy. */}
        {/* Equipment selection modal */}
        {showEquipmentModal && (
          <InventoryModal
            isOpen={showEquipmentModal}
            onClose={() => setShowEquipmentModal(false)}
            equipmentList={equipmentList}
            selectedIds={selectedEquipment}
            onToggle={(id) => setSelectedEquipment(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
          />
        )}

        {showSentOffersModal && (
          <SentOffersModal isOpen={showSentOffersModal} onClose={() => setShowSentOffersModal(false)} offers={sentOffers} />
        )}

        {showConfirmSendModal && (
          <ConfirmSendModal isOpen={showConfirmSendModal} onClose={() => setShowConfirmSendModal(false)} onConfirm={confirmSendEmail} email={form.email} t={t} />
        )}

        {showConfirmSendHistoryModal && offerToSendFromHistory && (
          <ConfirmSendModal
            isOpen={showConfirmSendHistoryModal}
            onClose={() => { setShowConfirmSendHistoryModal(false); setOfferToSendFromHistory(null); }}
            onConfirm={() => handleSendEmailFromHistory(offerToSendFromHistory)}
            email={offerToSendFromHistory.email}
            t={t}
          />
        )}

        {showDeleteConfirmModal && offerToDelete && (
          <ConfirmDeleteModal
            isOpen={showDeleteConfirmModal}
            onClose={() => { setShowDeleteConfirmModal(false); setOfferToDelete(null); }}
            onConfirm={confirmDeleteFromModal}
            offer={offerToDelete}
          />
        )}
      </Container>
    </>
  );
}

export default GenerateOffer;
